import { Bounds, Point } from "../engine/space/bounds";
import { RDisplay } from "../ui/RotDisplay";
import { gameData } from "./GameManager";
import { Item, ItemBreed } from "./items/Item";
import { Actor, ActorBreed, Piece } from "./actors/Actor";
import { Entity } from "../engine/entity/Entity";
import { ItemTypes } from "./items/itemsTypes";
import { runAction } from "./actions/Action";
import { Breed } from "../engine/entity/Breed";
import { piecePos } from "./words/wordUtils";

class EntityManager {
  private id = 1;

  getId() {
    const r = this.id;
    this.id += 1;
    return r;
  }

  resetIds() {
    this.id = 1;
  }

  addActorPieces(actor: Actor, direction: Point, word?: string) {
    const breed = actor.breed;

    if (breed.pieceBreed != null) {
      const pieceBreed = breed.pieceBreed;
      const initialPieces = word || breed.initialPieces;
      if (initialPieces) {
        let x = actor.x;
        let y = actor.y;
        for (let i = 0; i < initialPieces.length; i++) {
          const piece = this.createPiece(
            pieceBreed,
            x,
            y,
            actor.id,
            initialPieces[i]
          );
          actor.pieces.add(piecePos(piece), piece);
          x += direction.x;
          y += direction.y;
        }
      } else {
        const piece = this.createPiece(
          breed.pieceBreed,
          actor.x,
          actor.y,
          actor.id
        );
        actor.pieces.add(piecePos(piece), piece);
      }
    }
  }

  addActorChildren(actor: Actor) {
    const breed = actor.breed;

    breed.defaultContains &&
      breed.defaultContains.forEach(([type]) => {
        const containing = this.createItem(
          ItemTypes[type as keyof typeof ItemTypes]
        );
        containing.everHeld = true;
        actor.contains.add(containing.id, containing);
      });
  }

  createActor(breed: ActorBreed, x = 0, y = 0) {
    const newId = this.getId();
    const actor = new Actor(newId, breed, x, y);
    if (x != null || y != null) {
      gameData.actors.add(newId, actor);
    }

    actor.pieceDirection = { x: 1, y: 0 };
    this.addActorChildren(actor);
    this.addActorPieces(actor, actor.pieceDirection);

    return actor;
  }

  createItem(breed: ItemBreed, x?: number, y?: number) {
    const newId = this.getId();
    const actor = new Item(newId, breed, x, y);
    if (x != null || y != null) {
      gameData.items.add(newId, actor);
    }
    return actor;
  }

  createPiece(
    breed: Breed,
    x: number | null,
    y: number | null,
    owner = 0,
    char?: string
  ) {
    const newId = this.getId();
    const piece = new Piece(newId, breed, x || 0, y || 0, char);
    piece.owner = owner;
    if (x != null || y != null) {
      gameData.pieces.add(piecePos(piece), piece);
    }
    return piece;
  }

  drawEntities = (display: RDisplay, bounds: Bounds, view: Bounds) => {
    const result: Record<number, [Entity<any> | Piece, boolean]> = {};
    let existing, pos;
    gameData.items.forEach((item) => {
      if (
        item.x < view.x ||
        view.x + view.w <= item.x ||
        item.y < view.y ||
        view.y + view.h <= item.y
      ) {
        return;
      }
      pos = item.x + item.y * gameData.current.width;
      existing = result[pos];
      if (existing) {
        existing[1] = true;
        if (existing[0].breed.displayPriority < item.breed.displayPriority) {
          existing[0] = item;
        }
      } else {
        result[pos] = [item, false];
      }
    });

    gameData.pieces.forEach((actor) => {
      if (
        actor.x < view.x ||
        view.x + view.w <= actor.x ||
        actor.y < view.y ||
        view.y + view.h <= actor.y
      ) {
        return;
      }

      pos = actor.x + actor.y * gameData.current.width;
      existing = result[pos];
      if (existing) {
        existing[1] = true;
        if (existing[0].breed.displayPriority < actor.breed.displayPriority) {
          existing[0] = actor;
        }
      } else {
        result[pos] = [actor, false];
      }
    });

    gameData.letters.forEach((actor) => {
      if (
        actor.x < view.x ||
        view.x + view.w <= actor.x ||
        actor.y < view.y ||
        view.y + view.h <= actor.y
      ) {
        return;
      }
      pos = actor.x + actor.y * gameData.current.width;
      existing = result[pos];
      if (existing) {
        existing[1] = true;
        if (existing[0].breed.displayPriority < actor.breed.displayPriority) {
          existing[0] = actor;
        }
      } else {
        result[pos] = [actor, false];
      }
    });

    Object.values(result).forEach((foo) => {
      const actor = foo[0];
      display.drawAppend(
        actor.x + bounds.x - view.x,
        actor.y + bounds.y - view.y,
        actor.ch || actor.breed.ch,
        actor.breed.fg,
        actor.breed.bg || "transparent"
      );
    });
  };

  async runEntityQueue() {
    await gameData.actors.asyncForEach(async (a) => {
      const action = a.plan();
      if (action) {
        await runAction(action);
        await new Promise((r) => setTimeout(r, 80));
      }
    });
  }

  removeActorAndPieces(actor: Actor) {
    actor.pieces.forEach((p) => {
      if (p.owner === actor.id) {
        p.end();
        gameData.pieces.remove(piecePos(p));
      }
    });

    actor.end();
    gameData.actors.remove(actor.id);
  }
}

export default new EntityManager();
